/* =====================================================================
 * StartHR — shared domain & API types.
 * This file is THE contract between the React app, the MockProvider and
 * the Go API (api/internal/model mirrors these JSON shapes 1:1).
 * JSON field names here == JSON field names on the wire.
 * Dates are ISO strings: "YYYY-MM-DD"; timestamps "YYYY-MM-DDTHH:mm:ss" (+offset optional).
 * Money is a JSON number (2dp max) in the tenant's currency (Tenant.country → contracts/countries.json); billing is always SAR.
 * ===================================================================== */

export type ISODate = string;
export type ISODateTime = string;
export type Role = "owner" | "hr" | "accountant" | "employee" | "admin";
export type Lang = "en" | "ar";

export interface Page<T> { items: T[]; total: number; page: number; pageSize: number }

export interface ApiErrorBody { error: { code: string; message: string; fields?: Record<string, string> } }

/* ---------- Auth ---------- */
export interface User {
  id: string;
  email: string;
  name: string;
  nameAr: string;
  role: Role;
  tenantId: string;      // "platform" for admin
  employeeId: number | null; // linked employee for ESS
  /** Preferred language for emails (absent = en). */
  lang?: Lang;
  /** The company's country (in sign-in and /me responses; absent for the platform admin). */
  country?: CountryCode;
}
export interface LoginRequest { email: string; password: string; otp?: string }
export interface LoginResponse { token: string; user: User; otpRequired?: boolean }

/* ---------- Server clock ---------- */
/** Business clock of the server (KSA wall time). Pages must use this (via useToday/useClock), never the device date. */
export interface Clock { today: ISODate; now: ISODateTime }

/* ---------- Tenant / settings ---------- */
/** Business category keys (contracts/industries.json; Go model.BusinessCategories) in display order. */
export const BUSINESS_CATEGORIES = ["construction", "interior", "spareparts", "industrial", "software", "catering", "hospitality", "contracting", "trading", "hospital", "manpower", "engineering", "garage", "travel"] as const;
export type BusinessCategory = (typeof BUSINESS_CATEGORIES)[number];
export const isBusinessCategory = (k: unknown): k is BusinessCategory => typeof k === "string" && (BUSINESS_CATEGORIES as readonly string[]).includes(k);
/** GCC countries (contracts/countries.json; Go domain.Countries) in display order. Saudi Arabia is the default. */
export const COUNTRY_CODES = ["SA", "AE", "OM", "QA", "BH", "KW"] as const;
export type CountryCode = (typeof COUNTRY_CODES)[number];
export const isCountryCode = (k: unknown): k is CountryCode => typeof k === "string" && (COUNTRY_CODES as readonly string[]).includes(k);
/** category: "" until the company picks one (tenants created before categories existed).
 *  country: where the company employs its staff — drives currency, social insurance, end of service, WPS and ID rules
 *  (absent on tenants created before countries existed = "SA"). */
export interface Tenant { id: string; name: string; nameAr: string; cr: string; vat: string; city: string; category: BusinessCategory | ""; country?: CountryCode }
/** Social-insurance rates of the tenant's country (GOSI in Saudi Arabia, GPSSA, SPF, GRSIA, SIO, PIFSS elsewhere).
 *  sa* = citizen rates (citizen = employee nationality equals the tenant country), nsErOH / nsEmp = non-citizen employer / employee rates. */
export interface GosiSettings { saEmpAnn: number; saEmpSaned: number; saErAnn: number; saErSaned: number; saErOH: number; nsErOH: number; cap: number; nsEmp?: number }
export interface AttendancePolicy {
  shiftStart: string;   // "08:00"
  shiftEnd: string;     // "17:00"
  graceMinutes: number; // 15
  weekendDays: number[]; // 0=Sun..6=Sat ; Al Waha = [5] (6-day week)
  dedupeSeconds: number; // punches of same user within N seconds collapse (default 120)
  overtimeAfterMinutes: number; // OT counted after shift length + N minutes (default 0)
}
export interface Settings {
  tenant: Tenant;
  gosi: GosiSettings;
  allowances: { housing: number; transport: number };
  payDay: number;
  attendance: AttendancePolicy;
}

/* ---------- Employees ---------- */
export type EmployeeStatus = "Active" | "Probation" | "On leave" | "Terminated";
export interface Dependant { name: string; relation: string; insurance: string }
export interface EmpDocument { kind: string; number: string; expiry: ISODate; fileKey?: string }
export interface Employee {
  id: number;
  code: string;
  nameEn: string;
  nameAr: string;
  nationality: string; // ISO-2, "SA" = Saudi
  gender: "M" | "F";
  dept: string;        // key into lookups.DEPT
  title: string;
  branch: string;      // key into lookups.BR
  basic: number;
  housing: number;
  transport: number;
  other: number;
  joined: ISODate;
  status: EmployeeStatus;
  idNumber: string;    // 10 digits; starts 1 Saudi, 2 resident
  mobile: string;
  email: string;
  bank: string;
  iban: string;        // "SA" + 22 digits (spaces allowed)
  dob: ISODate;
  gosiRegime: "legacy" | "new";
  gosiWage: number;
  qiwa: { contractNo: string; status: string; basic: number };
  disabled: boolean;
  probationEnd: ISODate | null;
  cchiClass: string;
  dependants: Dependant[];
  ticket: { cls: string; dest: string; value: number } | null;
  iqamaExpiry: ISODate | null;
  documents: EmpDocument[];
  photoKey?: string;
}
export type EmployeeInput = Omit<Employee, "id" | "code" | "documents" | "dependants" | "qiwa" | "gosiWage" | "photoKey"> & {
  documents?: EmpDocument[];
  dependants?: Dependant[];
  qiwa?: Employee["qiwa"];
  gosiWage?: number;
};
export interface EmployeeQuery {
  q?: string; dept?: string; branch?: string; nat?: string; // nat: "SA" | "non" | ISO2
  status?: string; page?: number; pageSize?: number;
  sort?: "name" | "code" | "basic" | "joined" | "dept"; dir?: "asc" | "desc";
}
export interface ImportResult { created: number; skipped: number; errors: { row: number; message: string }[] }

export interface ExpiringDoc { employeeId: number; kind: string; number: string; expiry: ISODate; daysLeft: number }

/* ---------- Payroll ---------- */
export interface Adjustment { ot: number; bonus: number; absDays: number; ded: number }
export interface GosiBreakdown {
  base: number; regime: "legacy" | "new" | "non-saudi"; annRate: number;
  empAnn: number; empSaned: number; erAnn: number; erSaned: number; erOH: number; emp: number; er: number;
}
export interface Payslip {
  employeeId: number; period: string;
  basic: number; housing: number; transport: number; other: number;
  otHours: number; ot: number; bonus: number;
  absDays: number; abs: number; ded: number; loan: number;
  gosi: GosiBreakdown;
  earnings: number; deductions: number; net: number;
  cap: { limit: number; amount: number; over: boolean };
}
export interface PayrollHistoryItem { id: string; year: number; month: number; gross: number; employees: number; status: string; payDate: ISODate }
export interface PayrollRun {
  id: string; period: string; step: number; // 0 inputs,1 review,2 approve,3 export(WPS),4 paid
  approved: boolean; exported: boolean; paid: boolean; mudadAck: boolean;
  adjustments: Record<string, Adjustment>;     // keyed by employee id (string)
  exportFile?: FileRef | null;
  approvedBy?: string; approvedAt?: ISODateTime;
}
export interface MudadIssue { employeeId: number; kind: "GOSI" | "Qiwa"; registered: number; payroll: number; deviation: number }
export interface PayrollTotals { headcount: number; gross: number; deductions: number; net: number; gosiEmp: number; gosiEr: number; ot: number; bonus: number; employerCost: number }
export interface PayrollRunView { run: PayrollRun; slips: Payslip[]; totals: PayrollTotals; art92: number[]; mudad: MudadIssue[] }

/* ---------- Leave ---------- */
export type RequestStatus = "Pending" | "Approved" | "Rejected";
export interface Leave { id: number; employeeId: number; type: string; from: ISODate; to: ISODate; days: number; status: RequestStatus; note: string; attachmentKey?: string }
export interface LeaveInput { employeeId: number; type: string; from: ISODate; to: ISODate; note?: string; attachmentKey?: string }
export interface LeaveBalance { employeeId: number; entitlement: number; used: number; remaining: number }
export interface LeavePolicy { type: string; en: string; ar: string; ref: string }

/* ---------- Loans / letters / expenses / assets ---------- */
export interface Loan { id: number; employeeId: number; type: string; amount: number; months: number; paid: number; status: "Pending" | "Repaying" | "Settled" | "Rejected"; date: ISODate }
export interface LoanInput { employeeId: number; type: string; amount: number; months: number }
export interface Letter { id: number; employeeId: number; kind: string; to: string; status: "Pending" | "Issued"; date: ISODate; fileKey?: string }
export interface LetterInput { employeeId: number; kind: string; to: string }
export interface Expense { id: number; employeeId: number; vendor: string; category: string; date: ISODate; amount: number; vat: number; status: RequestStatus | "Reimbursed"; ai: boolean; receiptKey?: string }
export interface ExpenseInput { employeeId: number; vendor: string; category: string; date: ISODate; amount: number; vat: number; ai?: boolean; receiptKey?: string }
/* ---------- Work sites (industry pack) ---------- */
export type SiteKind = "project" | "client" | "property" | "warehouse" | "branch";
export interface WorkSite {
  id: string;            // "SITE-0001"
  name: string; nameAr: string; kind: SiteKind; client: string; city: string;
  start: ISODate | ""; end: ISODate | "";
  outdoor: boolean;      // outdoor work → summer midday work ban applies
  allowance: number;     // monthly site allowance per assigned employee (SAR)
  contractValue: number; // monthly contract / billing value (SAR), 0 = not set
  status: "Active" | "Closed";
  employeeIds: number[]; // an employee is on at most one Active site
}
export type WorkSiteInput = Partial<Omit<WorkSite, "id">>;

export interface Asset { tag: string; item: string; type: string; employeeId: number | null; serial: string; cost: number; status: string; warranty: ISODate }
export interface AssetInput { item: string; type: string; employeeId: number | null; serial: string; cost: number; warranty: ISODate }

/* ---------- Lifecycle ---------- */
export interface Onboarding { employeeId: number; start: ISODate; tasks: number[] }
export interface Offboarding { id: number; employeeId: number; type: string; submitted: ISODate; lastDay: ISODate; status: string; clearance: number[]; acknowledged: boolean }
export interface OffboardingInput { employeeId: number; type: string; lastDay: ISODate }
export interface EosbResult { wage: number; years: number; first: number; rest: number; full: number; factor: number; award: number }
export interface Settlement { salary: number; days: number; leaveDays: number; leavePay: number; eosb: number; eo: EosbResult; ticket: number; loan: number; ded: number; capped: boolean; net: number }

/* ---------- Government ---------- */
export interface GovTransaction { id: number; employeeId: number; type: string; period: number; fee: number; status: string; date: ISODate; ref: string }
export interface GovTransactionInput { employeeId: number; type: string; period: number }
export interface FeeResult { total: number; iq?: number; levy?: number; wp?: number }

/* ---------- Saudization ---------- */
export interface NitaqatResult { weighted: number; raw: number; total: number; pct: number; notDoc: number; under: number; docPct: number; docd: number; n: number; band: string }
export interface Band { key: string; to: number; tone: string }

/* ---------- Misc lists ---------- */
export interface Case { id: number; employeeId: number; kind: string; title: string; step: string; opened: ISODate; due: ISODate; status: string }
export interface Candidate { id: number; name: string; role: string; stage: string; score: number }
export interface Goal { id: number; title: string; employeeId: number; progress: number }
export interface AuditEntry { id: string; at: ISODateTime; actor: string; action: string; entity: string; entityId: string; detail: string }
export interface Approval { kind: "Leave" | "Loan" | "Letter" | "Expense" | "Resignation"; id: string; refId: number; employeeId: number; summary: string; when: string; slaDays: number }
export interface Notification { id: string; at: ISODateTime; title: string; body: string; route: string; tone: string; read: boolean }

/* ---------- Files (S3) ---------- */
export interface FileRef { key: string; url: string; name: string; size: number; contentType: string }

/* ---------- Billing ---------- */
export type Cycle = "m" | "y";
export interface Subscription { plan: string; cycle: Cycle; seats: number; status: "trial" | "pending" | "active" | "expired"; trialStart: ISODate; trialDays: number; activeUntil: ISODate | null; aiPack: number }
export interface Payment {
  id: string; tenant: string; tenantId?: string; cr: string; plan: string; cycle: Cycle; seats: number;
  amount: number; vat: number; total: number; ref: string; date: ISODate; bank: string; sender: string; paid: number;
  status: "Pending" | "Verified" | "Rejected"; reason?: string; by?: string; at?: ISODate;
  receiptKey?: string; ai?: { amount: number; date: ISODate; ref: string; conf: number } | null;
}
export interface PaymentInput { plan: string; cycle: Cycle; seats: number; aiPack: number; bank: string; sender: string; paid: number; date: ISODate; receiptKey?: string }
export interface BankAccount { bank: string; bankAr: string; name: string; nameAr: string; iban: string; acct: string; active: boolean }
export interface BillingView { subscription: Subscription; trialEnd: ISODate; trialLeft: number; payments: Payment[]; bankAccounts: BankAccount[]; planPrices: Record<string, number>; tenant: Tenant }
export interface TenantSummary { id: string; name: string; cr: string; city: string; plan: string; seats: number; status: string; since: ISODate; aiCredits: number; category: BusinessCategory | ""; country?: CountryCode }
export interface AdminOverview { tenants: number; trials: number; active: number; pendingPayments: number; mrr: number; aiCreditsUsed: number }

/* ---------- AI ---------- */
export interface AiProvider { id: string; name: string; region: string; keyMasked: string; connected: boolean; models: string[]; pricePerMTok: Record<string, number> }
export interface AiFeature { id: string; name: string; nameAr: string; where: string; icon: string; model: string; pii: boolean; saveMinutes: number; uses: number; enabled: boolean; description: string; descriptionAr: string }
export interface AiConfig { providers: AiProvider[]; residency: boolean; budget: number; credits: Record<string, number>; used: number; limit: number; features: AiFeature[] }
export interface AiExtractRequest { feature: string; fileKey?: string; text?: string; sample?: string }
export interface AiExtractResponse { feature: string; fields: Record<string, string | number | boolean | null>; confidence: number; model: string; creditsUsed: number }
export interface AiChatRequest { message: string; lang: Lang }
export interface AiChatResponse { reply: string; model: string; creditsUsed: number }

/* =====================================================================
 * Biometric attendance (vendor-agnostic)
 * ===================================================================== */
export type BioVendor = "zkteco" | "zkbiotime" | "suprema" | "hikvision" | "dahua" | "anviz" | "generic" | "agent";
export type BioProtocol = "push" | "poll" | "webhook" | "agent" | "file";
export type PunchDirection = "in" | "out" | "break_out" | "break_in" | "ot_in" | "ot_out" | "unknown";
export type VerifyMode = "fingerprint" | "face" | "card" | "pin" | "palm" | "iris" | "unknown";

export interface BioVendorInfo {
  id: BioVendor; name: string; protocols: BioProtocol[]; models: string[];
  setup: string[]; setupAr: string[]; realtime: boolean;
}
export interface Device {
  id: string;
  name: string;
  vendor: BioVendor;
  protocol: BioProtocol;
  model: string;
  serial: string;             // device SN — unique per platform (push devices identify by SN)
  branch: string;
  timezone: string;           // IANA, default "Asia/Riyadh"
  status: "online" | "offline" | "pending";
  claimed: boolean;           // false = auto-discovered push device waiting for claim
  lastSeen: ISODateTime | null;
  lastPunchAt: ISODateTime | null;
  ip: string;
  firmware: string;
  userCount: number;
  punchCount: number;
  directionMode: "device" | "first-in-last-out" | "alternate"; // how to derive in/out when device sends none
  pollIntervalSec: number;
  baseUrl: string;            // pull connectors (BioStar/ISAPI/Dahua/CrossChex/BioTime)
  username: string;
  hasSecret: boolean;         // password/api-key stored encrypted server-side, never returned
  webhookToken: string;       // for generic webhook/agent ingestion (shown once, masked afterwards)
  cursor: string;             // poll connectors: last fetched position
  createdAt: ISODateTime;
}
export interface DeviceInput {
  name: string; vendor: BioVendor; protocol: BioProtocol; model?: string; serial: string; branch: string;
  timezone?: string; directionMode?: Device["directionMode"]; pollIntervalSec?: number;
  baseUrl?: string; username?: string; secret?: string;
}
export interface Punch {
  id: string;
  deviceId: string;
  deviceUserId: string;       // PIN / user id on the device
  employeeId: number | null;  // resolved via mapping; null = unmatched
  time: ISODateTime;          // device-local wall clock "YYYY-MM-DDTHH:mm:ss"
  direction: PunchDirection;
  verify: VerifyMode;
  source: BioProtocol | "manual" | "ess";
  dedupeKey: string;          // deviceId|deviceUserId|time|direction
  receivedAt: ISODateTime;
  raw: string;
}
export interface PunchQuery { date?: ISODate; from?: ISODate; to?: ISODate; deviceId?: string; employeeId?: number; unmatched?: boolean; page?: number; pageSize?: number }
export interface DeviceUserMap { deviceId: string; deviceUserId: string; employeeId: number }  // deviceId "*" = all devices
export interface UnmatchedUser { deviceId: string; deviceUserId: string; name: string; punches: number; firstSeen: ISODateTime; lastSeen: ISODateTime }
export interface DeviceCommand {
  id: number; deviceId: string;
  kind: "sync_user" | "delete_user" | "reboot" | "query_logs" | "info" | "set_time" | "clear_logs";
  payload: Record<string, string>;
  status: "queued" | "sent" | "done" | "failed";
  createdAt: ISODateTime; sentAt: ISODateTime | null; doneAt: ISODateTime | null; result: string;
}
export interface IngestResult { accepted: number; duplicates: number; unmatched: number; rejected: number; errors: string[] }
export interface PunchInput { deviceUserId: string; time: ISODateTime; direction?: PunchDirection | string | number; verify?: VerifyMode | string | number; raw?: string }

export type DayStatus = "Present" | "Late" | "Absent" | "On leave" | "Weekend" | "Missing punch" | "Not in yet";
export interface DailyAttendance {
  employeeId: number; date: ISODate;
  firstIn: string | null;   // "HH:mm"
  lastOut: string | null;   // "HH:mm"
  workedMinutes: number; lateMinutes: number; overtimeMinutes: number;
  punches: number; status: DayStatus;
  source: string;           // device name or "Mobile · geofence" / "Manual"
}
export interface AttendanceDay { date: ISODate; rows: DailyAttendance[]; summary: Record<DayStatus, number> & { overtimeHours: number } }
export interface CheckRequest { employeeId: number; kind: "in" | "out"; lat?: number; lng?: number }
export interface ConnectionInfo {
  pushHost: string;        // host devices should use for ZK ADMS cloud server, e.g. "hr.starthr.gulfunionozone.com"
  pushPort: number;        // 443 (https) or 80
  https: boolean;
  pushPath: string;        // "/iclock/cdata"
  webhookBase: string;     // e.g. "https://hr.starthr.gulfunionozone.com/api/v1/ingest/" (+ deviceId)
  agentDownloadUrl: string;
  timezoneOffset: number;  // 3 for KSA — value devices should use for "TimeZone"
}
export interface BiometricOverview { devices: number; online: number; offline: number; pending: number; punchesToday: number; unmatched: number; lastPunchAt: ISODateTime | null }

/* =====================================================================
 * Self-signup, email verification, password reset
 * ===================================================================== */
export interface SignupRequest {
  companyName: string; companyNameAr?: string; cr: string; vat?: string; city: string; category?: BusinessCategory | ""; country?: CountryCode;
  ownerName: string; email: string; password: string; plan: string; lang: Lang;
}
export interface SignupResponse { pendingVerification: true; email: string }
export interface InviteResult { ok: boolean; userId: string; email: string }

/** One notification preference of the tenant (GET/PUT /settings/notifications). */
export interface NotificationPref { key: string; group: "billing" | "hr" | "compliance"; enabled: boolean }

/* =====================================================================
 * Platform admin: storage
 * ===================================================================== */
export type StorageMode = "s3" | "local" | "memory";
export interface StorageStatus { ok: boolean; message: string; checkedAt: ISODateTime }
export interface StorageConfig {
  mode: StorageMode; bucket: string; region: string; endpoint: string; pathStyle: boolean; prefix: string;
  /** Masked (first 6 + •••••••• + last 4) — never the full key. */
  accessKeyId: string;
  hasSecret: boolean;
  /** True only for environment defaults without keys (S3_ACCESS_KEY empty). */
  useInstanceRole: boolean;
  presign: boolean;
  /** Local folder (STORAGE_DIR) when mode is local. */
  dir: string;
  source: "env" | "database";
  status: StorageStatus;
  stats?: { objects?: number; lastError?: string };
  updatedAt?: string; updatedBy?: string;
}
export interface StorageInput {
  /** For s3: bucket, region, accessKeyId and secretAccessKey are required (422 "required").
   *  endpoint (S3-compatible services), pathStyle and prefix are optional extras. */
  mode: StorageMode; bucket?: string; region?: string; endpoint?: string; pathStyle?: boolean; prefix?: string;
  /** The masked key from StorageConfig keeps the stored key. */
  accessKeyId?: string;
  /** Write-only. Empty keeps the stored secret when the access key is unchanged. */
  secretAccessKey?: string;
  /** Ignored — dashboard-saved S3 settings always use keys (the EC2 instance role is an environment default only). */
  useInstanceRole?: boolean;
  presign?: boolean;
  /** Existing files are never moved (only false is accepted). */
  migrate?: false;
  /** Save even when the connection check fails. */
  force?: boolean;
}
/** S3: credentials → bucket → write → read → delete. Local/memory: connect → write → read → delete. */
export interface StorageTestStep { step: "credentials" | "bucket" | "connect" | "write" | "read" | "delete"; ok: boolean; ms: number; error?: string; hint?: string; skipped?: boolean }
export interface StorageTestResult { ok: boolean; mode: StorageMode; steps: StorageTestStep[] }

/* =====================================================================
 * Platform admin: email
 * ===================================================================== */
export type EmailProviderId = "ses" | "ses-smtp" | "zoho" | "zeptomail" | "office365" | "gmail" | "smtp" | "sendgrid" | "mailgun" | "postmark" | "brevo"
  | "mandrill" | "sparkpost" | "resend" | "elasticemail" | "log";
export type SmtpSecurity = "starttls" | "tls" | "none";
export interface EmailProviderSettings {
  provider: EmailProviderId; host: string; port: number; security: SmtpSecurity | ""; authMethod: "plain" | "login" | "";
  username: string; region: string; domain: string; messageStream: string; endpoint: string;
  /** The secret (password / API key) is write-only. */
  hasSecret: boolean;
}
export interface EmailConfig {
  provider: EmailProviderSettings; fallback: EmailProviderSettings | null;
  fromName: string; fromAddress: string; replyTo: string; sandboxTo: string; dailyLimit: number;
  source: "env" | "database"; sentToday: number; queued: number; updatedAt?: string; updatedBy?: string;
}
export interface EmailProviderInput {
  provider: EmailProviderId; host?: string; port?: number; security?: SmtpSecurity; authMethod?: "plain" | "login";
  username?: string; /** empty = keep the stored secret */ secret?: string; region?: string; domain?: string; messageStream?: string; endpoint?: string;
}
export interface EmailConfigInput {
  provider: EmailProviderInput; fallback?: EmailProviderInput | null;
  fromName: string; fromAddress: string; replyTo?: string; sandboxTo?: string; dailyLimit?: number;
}
export interface EmailTestRequest { to: string; lang?: Lang; config?: EmailConfigInput }
export interface EmailTestResult {
  ok: boolean; provider: string; providerMessageId: string; ms: number; to: string; logId: string;
  error?: string; errorKind?: "auth" | "sender" | "recipient" | "tls" | "dns" | "network" | "rate" | "config" | "provider" | "sandbox"; hint?: string;
}
export type EmailStatus = "queued" | "sending" | "sent" | "failed" | "suppressed";
export interface EmailLog {
  id: string; tenantId: string; tenant: string; to: string; toName: string; lang: Lang | "bi"; template: string; category: string; essential: boolean;
  subject: string; status: EmailStatus; attempts: number; nextAttemptAt: ISODateTime; providerId: string; provider: string; error: string;
  createdAt: ISODateTime; sentAt: ISODateTime | null; redirectedFrom?: string;
}
/** Bodies are only returned while the Log-only provider is active (developer mailbox). */
export interface EmailLogDetail extends EmailLog { html?: string; text?: string; bodyHidden: boolean }
export interface EmailLogQuery { status?: EmailStatus | ""; template?: string; to?: string; page?: number; pageSize?: number }
export interface EmailTemplateInfo { key: string; category: string; essential: boolean; pref: string | null; subject: { en: string; ar: string } }
export interface EmailPreview { key: string; lang: Lang | "bi"; subject: string; preheader: string; html: string; text: string }
export interface DnsRecord { type: "TXT" | "CNAME" | "MX"; host: string; value: string; purpose: "spf" | "dkim" | "dmarc" | "return-path" | "verification"; note: string; noteAr: string }
export interface DnsGuidance { domain: string; provider: string; records: DnsRecord[] }
export interface Suppression { email: string; reason: "bounce" | "complaint" | "unsubscribe" | "manual"; note: string; createdAt: ISODateTime }
